import { Button, Card, Col, Divider, Row, Space } from "antd";
import { useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import SkipReasonContext from "../../../context/skip-reason/context";
import SkipReasonContextProvider from "../../../context/skip-reason/provider";
import { IUpdateSkipReason } from "../../../models/skip-reason/request";
import EndPoints from "../../../services/end-points";
import { execute } from "../../../utils/helpers/execute";
import FieldBuilder from "../../form-components/field-builder";
import Spin from "../../general/antd/spin";
import styles from "./style.module.scss";

const Form = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { details, actions, loading } = useContext(SkipReasonContext);
  const { id } = useParams();

  const methods = useForm<IUpdateSkipReason>({
    defaultValues: {
      code: details?.code || "",
      ar_name: details?.arName || "",
      en_name: details?.enName || "",
      is_store_skip: details?.isStoreSkip || false,
      is_active: details?.isActive ?? true,
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  useEffect(() => {
    id && actions.getDetails(Number(id));
  }, [id]);

  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const onSubmit = async (data: IUpdateSkipReason) => {
    await execute({
      callback: async () => {
        setLoadingSubmit(true);
        if (details) {
          await EndPoints.skipReason.updateSkipReason(details.id, data);
        } else {
          await EndPoints.skipReason.createSkipReason(data);
        }
        navigate(-1);
      },
      fallback: (error) => {},
      finallyCallback: () => {
        setLoadingSubmit(false);
      },
      throwException: false,
    });
  };

  useEffect(() => {
    reset({
      code: details?.code || "",
      ar_name: details?.arName || "",
      en_name: details?.enName || "",
      is_store_skip: details?.isStoreSkip || false,
      is_active: details?.isActive ?? true,
    });
  }, [details]);

  return (
    <FormProvider {...methods}>
      <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
        <div>
          <span className={styles.title}>
            {details ? t("update_skip_reason") : t("add_skip_reason")}
          </span>
          <Divider className={styles.divider} />
          {loading.includes("details") && id ? (
            <div style={{ height: "60vh" }}>
              <Spin />
            </div>
          ) : (
            <div className={styles.children}>
              <Space direction="vertical" size="large" style={{ width: "100%" }}>
                {/* Skip Reason Information Section */}
                <Card title={t("skip_reason_information")} bordered={false}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                      <FieldBuilder
                        name="code"
                        label={t("code")}
                        input={{ type: "text" }}
                        rules={{ required: true }}
                      />
                    </Col>

                    <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                      <FieldBuilder
                        name="ar_name"
                        label={t("ar_name")}
                        input={{ type: "text" }}
                        rules={{ required: true }}
                      />
                    </Col>

                    <Col xs={24} sm={24} md={12} lg={12} xl={12} xxl={12}>
                      <FieldBuilder
                        name="en_name"
                        label={t("en_name")}
                        input={{ type: "text" }}
                        rules={{ required: true }}
                      />
                    </Col>
                  </Row>
                </Card>

                {/* Skip Type Settings Section */}
                <Card title={t("skip_type_settings")} bordered={false}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                      <Space direction="vertical" size="small" style={{ width: "100%" }}>
                        <FieldBuilder
                          name="is_store_skip"
                          label={t("is_store_skip")}
                          input={{
                            type: "switch",
                          }}
                          rules={{ required: false }}
                        />
                        <small style={{ color: "#888", marginTop: 4 }}>
                          {t("is_store_skip_help")}
                        </small>
                      </Space>
                    </Col>

                    <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                      <Space direction="vertical" size="small" style={{ width: "100%" }}>
                        <FieldBuilder
                          name="is_active"
                          label={t("is_active")}
                          input={{
                            type: "switch",
                          }}
                          rules={{ required: false }}
                        />
                        <small style={{ color: "#888", marginTop: 4 }}>
                          {t("is_active_skip_reason_help")}
                        </small>
                      </Space>
                    </Col>
                  </Row>
                </Card>
              </Space>
            </div>
          )}
        </div>

        <div>
          <Divider className={styles.divider} />
          <div className={styles.formFooter}>
            <Button
              onClick={() => {
                navigate(-1);
              }}
            >
              {t("cancel")}
            </Button>
            <Button loading={loadingSubmit} htmlType="submit" type="primary">
              {t("save")}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

const SkipReasonForm = () => {
  return (
    <SkipReasonContextProvider>
      <Form />
    </SkipReasonContextProvider>
  );
};

export default SkipReasonForm;
